import { FC, ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface TablePopoverProps {
    anchorRect: DOMRect | null;
    onClose: () => void;
    children: ReactNode;
}

const POPOVER_GAP_PX = 6;
// Higher than the modal overlay/toast z-index (99999) used elsewhere in the app,
// since QuillInput is commonly rendered inside a modal (e.g. BaseModal/CustomPopup).
const POPOVER_Z_INDEX = 999999;

// Anchored, portal-rendered popover used for the Quill table toolbar button.
// Positioned relative to the toolbar button rect rather than a React-owned trigger,
// since the button itself is rendered by Quill outside of React's control.
const TablePopover: FC<TablePopoverProps> = ({ anchorRect, onClose, children }) => {
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    if (!anchorRect) return null;

    return createPortal(
        <div
            ref={popoverRef}
            // Stops clicks inside this portal (rendered outside any surrounding modal's DOM
            // subtree) from reaching document-level "click outside" listeners, e.g. the
            // modal's own, which would otherwise treat this as an outside click and close it.
            onMouseDown={(event) => event.stopPropagation()}
            style={{
                position: "fixed",
                top: anchorRect.bottom + POPOVER_GAP_PX,
                left: anchorRect.left,
                zIndex: POPOVER_Z_INDEX,
            }}
            className="bg-white dark:bg-dark-bg-300 dark:text-dark-text-100 rounded-lg shadow-md p-3"
        >
            {children}
        </div>,
        document.body
    );
};

export default TablePopover;
